import React, { useState, useEffect, useRef } from 'react';
import { Form, Row, Col } from 'react-bootstrap';
import { Button, Select, InputNumber, DatePicker } from 'antd';
import { PlusCircle, DashCircle } from 'react-bootstrap-icons';
import dayjs from 'dayjs';
import Api from "../services/api";
import {useParams} from "react-router-dom";
import { MinusOutlined, PlusOutlined } from '@ant-design/icons';

const FilterCampaignCenter = ({ startDate, endDate, handleChangeDate, id, onRunReport }) => {
    const { code, os } = useParams();
    const [dimensionFilters, setDimensionFilters] = useState([
        { key: '', operator: 'equals', value: [], options: [] },
    ]);

    const [metricFilters, setMetricFilters] = useState([
        { key: '', operator: 'between', min: '', max: '' },
    ]);

    const containerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                const allPopups = document.querySelectorAll('[data-popup-id]');
                allPopups.forEach(popup => {
                    if (popup.id !== id) {
                        popup.classList.remove('show');
                    }
                });
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [id]);

    const handleRunReport = () => {
        const filterData = {
            startDate,
            endDate,
            dimensionFilters,
            metricFilters,
        };
        if (onRunReport) onRunReport(filterData);
    };

    const fetchOptionsByDimension = async (dimensionKey, index) => {
        try {
            // Format dates từ Date Range
            const fromDate = startDate ? dayjs(startDate).format('YYYY-MM-DD') : '';
            const toDate = endDate ? dayjs(endDate).format('YYYY-MM-DD') : '';
            
            // Build API params
            const params = {
                dimension: dimensionKey,
                code: code,
                os: os
            };
            
            // Thêm from và to nếu có
            if (fromDate) {
                params.from = fromDate;
            }
            if (toDate) {
                params.to = toDate;
            }
            
            const response = await Api.get(`/api/app/dimension_filter`, { params });
            const options = Array.isArray(response.data.data) ? response.data.data : [];
            const updated = [...dimensionFilters];
            updated[index].options = options;
            setDimensionFilters(updated);
        } catch (error) {
            console.error("Failed to fetch options for dimension:", dimensionKey);
            const updated = [...dimensionFilters];
            updated[index].options = [];
            setDimensionFilters(updated);
        }
    };

    const handleDimensionChange = (index, field, value) => {
        const updated = [...dimensionFilters];
        updated[index][field] = value;
        if (field === 'key') {
            updated[index].value = [];
            updated[index].options = [];
            fetchOptionsByDimension(value, index);
        }
        setDimensionFilters(updated);
    };

    const handleMetricChange = (index, field, value) => {
        const updated = [...metricFilters];
        updated[index][field] = value;
        setMetricFilters(updated);
    };

    const handleRemoveDimension = (index) => {
        if (dimensionFilters.length > 1) {
            const updated = dimensionFilters.filter((_, i) => i !== index);
            setDimensionFilters(updated);
        }
    };

    const handleRemoveMetric = (index) => {
        if (metricFilters.length > 1) {
            const updated = metricFilters.filter((_, i) => i !== index);
            setMetricFilters(updated);
        }
    };

    const handleAddDimension = () => {
        setDimensionFilters([
            ...dimensionFilters,
            { key: '', operator: 'equals', value: [], options: [] },
        ]);
    };

    const handleAddMetric = () => {
        setMetricFilters([
            ...metricFilters,
            { key: '', operator: 'between', min: '', max: '' },
        ]);
    };

    const handleMultiSelectChange = (index, selectedOptions) => {
        const updated = [...dimensionFilters];
        updated[index].value = selectedOptions.map(opt => opt.value); // nếu multi
        setDimensionFilters(updated);
    };

    const dimension_array = [
        { key: "country", name: "Country" },
        { key: "campaign_id", name: "Campaign" },
        // { key: "ad_set_id", name: "Ad Group" },
        { key: "media_source", name: "Ad Network" },
        { key: "ad_group", name: "Ad Group" }
    ];

    const metric_array = [
        { key: "cost", name: "Cost" },
        { key: "install_ad_network", name: "Ad Network Installs" },
        { key: "ecpi_ad_network", name: "Ad Network eCPI" },
        { key: "count_session", name: "Sessions" },
        { key: "count_impression", name: "Impressions" },
        // { key: "roas_d0", name: "ROAS D0" },
        { key: "ad_revenue", name: "Ad Revenue"},
        { key: "iap_revenue", name: "IAP Revenue" },
        { key: "daily_pu", name: "Daily Paid Users" },
        { key: "RR_D1", name: "RR D1" },
        { key: "RR_D3", name: "RR D3" },
        { key: "RR_D7", name: "RR D7" },
        { key: "RR_D14", name: "RR D14" },
        { key: "paid_ratepaid_rate", name: "Paid Rate" },
    ];

    return (
        <div  ref={containerRef} data-popup-id={id} id={id} className="filter-campaign-center" style={{ padding: '0px !important' }}>
            <div className="row" style={{ marginBottom: '15px' }}>
                <div className="col-md-2">
                    <b>Date Range</b>
                </div>
                <div className='col-md-10'>
                    <div className="col-md-3" style={{ paddingLeft: '0px', paddingRight: '18px' }}>
                        <DatePicker.RangePicker
                            value={[dayjs(startDate), dayjs(endDate)]}
                            onChange={(dates) => {
                                if (!dates || dates.length !== 2) return;
                                const [start, end] = dates;
                                handleChangeDate(null, { startDate: start, endDate: end });
                            }}
                            allowClear={false}
                            format="YYYY-MM-DD"
                            style={{ width: '100%' }}
                            getPopupContainer={(trigger) => trigger.parentNode}
                            dropdownClassName="drawer-date-dropdown"
                        />
                    </div>
                </div>
            </div>

            <div className="row" style={{ marginBottom: '15px' }}>
                <div className="col-md-2">
                    <b>Filter by dimension</b>
                </div>
                <div className='col-md-10'>
                    {dimensionFilters.map((filter, index) => {
                        const usedKeys = dimensionFilters.map((f, i) => i !== index && f.key);
                        const availableDimensions = dimension_array.filter(
                            dim => !usedKeys.includes(dim.key) || dim.key === filter.key
                        );

                        return (
                            <Row className="mb-2 filter-line" key={`dim-${index}`}>
                                <Col md={3}>
                                    <Select
                                        value={filter.key || undefined}
                                        className="w-100"
                                        placeholder="Select a dimension"
                                        onChange={(value) => handleDimensionChange(index, 'key', value)}
                                        allowClear
                                    >
                                        {availableDimensions.map(dim => (
                                            <Select.Option key={dim.key} value={dim.key}>{dim.name}</Select.Option>
                                        ))}
                                    </Select>
                                </Col>

                                <Col md={2}>
                                    <Select
                                        value={filter.operator}
                                        className="w-100"
                                        onChange={(value) => handleDimensionChange(index, 'operator', value)}
                                    >
                                        <Select.Option value="equals">equals</Select.Option>
                                        <Select.Option value="not_equal">not equal</Select.Option>
                                    </Select>
                                </Col>

                                <Col md={3}>
                                    <Select
                                        mode="multiple"
                                        value={filter.value || []}
                                        onChange={(values) => {
                                            // Convert values to format expected by handleMultiSelectChange
                                            const selectedOptions = (filter.options || []).filter(opt => 
                                                values.includes(opt.value || opt.code)
                                            ).map(opt => ({
                                                value: opt.value || opt.code,
                                                label: opt.label || opt.name
                                            }));
                                            handleMultiSelectChange(index, selectedOptions);
                                        }}
                                        placeholder="Select"
                                        className="w-100"
                                        allowClear
                                        showSearch
                                        filterOption={(input, option) => {
                                            const label = option?.label || option?.children || '';
                                            return label.toLowerCase().includes(input.toLowerCase());
                                        }}
                                    >
                                        {(filter.options || []).map(opt => (
                                            <Select.Option 
                                                key={opt.value || opt.code} 
                                                value={opt.value || opt.code}
                                                label={opt.label || opt.name}
                                            >
                                                {opt.label || opt.name}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Col>

                                <Col md={1} className="d-flex align-items-center">
                                    {dimensionFilters.length > 1 && (
                                        <Button
                                            size='small'
                                            type="dashed" shape="circle"
                                            danger
                                            icon={<MinusOutlined />}
                                            onClick={() => handleRemoveDimension(index)}
                                            style={{ marginRight: '10px' }}
                                        />
                                    )}

                                    <Button
                                        size='small'
                                        type="dashed" shape="circle"
                                        icon={<PlusOutlined />}
                                        onClick={handleAddDimension}
                                    />
                                </Col>
                            </Row>
                        );
                    })}
                </div>
            </div>

            <div className="row" style={{ marginBottom: '15px' }}>
                <div className="col-md-2">
                    <b>Filter by metric</b>
                </div>
                <div className='col-md-10'>
                    {metricFilters.map((filter, index) => {
                        const selectedMetricKeys = metricFilters
                            .map(f => f.key)
                            .filter(Boolean);
                        const availableMetrics = metric_array.filter(metric =>
                            !selectedMetricKeys.includes(metric.key) || metric.key === filter.key
                        );
                        return (
                        <Row className="mb-2 filter-line" key={`met-${index}`}>
                            <Col md={3}>
                                <Select
                                    value={filter.key || undefined}
                                    className="w-100"
                                    placeholder="Select a metric"
                                    onChange={(value) => handleMetricChange(index, 'key', value)}
                                    allowClear
                                >
                                    {availableMetrics.map(dim => (
                                        <Select.Option key={dim.key} value={dim.key}>{dim.name}</Select.Option>
                                    ))}
                                </Select>
                            </Col>

                            <Col md={2}>
                                <Select
                                    value={filter.operator}
                                    className="w-100"
                                    onChange={(value) => handleMetricChange(index, 'operator', value)}
                                >
                                    <Select.Option value="between">between</Select.Option>
                                    <Select.Option value="except">except</Select.Option>
                                </Select>
                            </Col>

                            <Col md={2}>
                                <InputNumber
                                    className="w-100"
                                    placeholder="Min"
                                    value={filter.min ? (isNaN(parseFloat(filter.min)) ? undefined : parseFloat(filter.min)) : undefined}
                                    onChange={(value) => handleMetricChange(index, 'min', value !== null && value !== undefined ? String(value) : '')}
                                />
                            </Col>
                            <Col md={2}>
                                <InputNumber
                                    className="w-100"
                                    placeholder="Max"
                                    value={filter.max ? (isNaN(parseFloat(filter.max)) ? undefined : parseFloat(filter.max)) : undefined}
                                    onChange={(value) => handleMetricChange(index, 'max', value !== null && value !== undefined ? String(value) : '')}
                                />
                            </Col>

                            <Col md={1} className="d-flex align-items-center">
                                {metricFilters.length > 1 && (
                                    <Button
                                        size='small'
                                        type="dashed" shape="circle"
                                        danger
                                        icon={<MinusOutlined />}
                                        onClick={() => handleRemoveMetric(index)}
                                        style={{ marginRight: '10px' }}
                                    />
                                )}
                                <Button
                                    size='small'
                                    type="dashed" shape="circle"
                                    icon={<PlusOutlined />}
                                    onClick={handleAddMetric}
                                />
                            </Col>
                        </Row>
                    )})}
                </div>
            </div>
            <div className="row" style={{ marginBottom: '15px' }}>
                <div className="col-md-3">
                    <Button type="primary" onClick={handleRunReport}>Run Report</Button>
                </div>
            </div>
            <style>{`
                @media (max-width: 768px) {
                  /* Vertical spacing 10px between items in dimension/metric sections */
                  .filter-campaign-center .filter-line > [class*="col-"] + [class*="col-"] { margin-top: 10px !important; }
                  .filter-campaign-center .filter-line > .col + .col { margin-top: 10px !important; }
                }
            `}</style>
        </div>
    );
};

export default FilterCampaignCenter;


